import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";

const COPY = {
  livingabroad: {
    title: "Thinking about living abroad?",
    blurb: "Get relocation tips, overseas property leads and expat guidance straight to your inbox.",
    button: "Get Started",
  },
  rv: {
    title: "Ready to hit the road?",
    blurb: "Get RV deals, buying advice and adventure inspiration. Tell us what you're looking for.",
    button: "Send Request",
  },
  business: {
    title: "Explore business opportunities",
    blurb: "Starting out, buying a franchise or looking to invest? Tell us what interests you and we'll send the right opportunities your way.",
    button: "Send Me Info",
    interests: ["Starting a business", "Franchise", "Investing", "Partnership", "Other"],
  },
  default: {
    title: "Get in touch",
    blurb: "Leave your details and we'll be in touch.",
    button: "Submit",
  },
};

export const LeadCaptureForm = ({ channel }) => {
  const copy = COPY[channel] || COPY.default;
  const [form, setForm] = useState({ name: "", email: "", message: "", interest: copy.interests?.[0] || "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", { channel, ...form });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="px-6 md:px-10" data-testid="lead-capture">
      <div className="relative overflow-hidden rounded-lg border border-white/10 bg-gradient-to-br from-[#1a0f0f] via-[#0A0A0F] to-[#0A0A0F] p-8 md:p-12">
        <div className="max-w-xl">
          <p className="text-xs tracking-[0.25em] uppercase text-[#FF2A2A] mb-3">Stay In The Loop</p>
          <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-white">{copy.title}</h3>
          <p className="mt-3 text-sm text-zinc-400">{copy.blurb}</p>

          {done ? (
            <div className="mt-6 flex items-center gap-3 text-white" data-testid="lead-success">
              <CheckCircle2 className="w-6 h-6 text-[#FF2A2A]" />
              <span className="text-sm">Thanks! We've got your details and will be in touch soon.</span>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  data-testid="lead-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className="bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
                />
                <input
                  data-testid="lead-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email address"
                  className="bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
                />
              </div>
              {copy.interests && (
                <select
                  data-testid="lead-interest"
                  aria-label="I'm interested in"
                  value={form.interest}
                  onChange={set("interest")}
                  className="w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A]"
                >
                  {copy.interests.map((i) => (
                    <option key={i} value={i}>I'm interested in: {i}</option>
                  ))}
                </select>
              )}
              <textarea
                data-testid="lead-message"
                value={form.message}
                onChange={set("message")}
                placeholder="What are you looking for? (optional)"
                rows={3}
                className="w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] resize-none"
              />
              <button
                data-testid="lead-submit"
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-7 py-3 rounded-sm font-semibold transition-colors"
              >
                <Send className="w-4 h-4" /> {copy.button}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
